import { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";

const useDrillMode = (
  sceneRef,
  cameraRef,
  modelRef,
  containerRef,
  undoStack,
  undoIndex,
  viewportRef = null
) => {
  const drillBrushRef = useRef(null);
  const [isDrillMode, setIsDrillMode] = useState(false);
  const [brushActive, setBrushActive] = useState(false);
  const [brushSize, setBrushSize] = useState(1);
  const [drillShape, setDrillShape] = useState("circle");
  const drillDepth = 1;

  const initializeBrush = useCallback(
    (size, thickness = 5, shape = "circle") => {
      const brushSegments = [];

      if (shape === "circle") {
        const segments = 50;
        for (let i = 0; i < segments; i++) {
          const nexti = (i + 1) % segments;
          const x1 = Math.sin((2 * Math.PI * i) / segments) * size;
          const y1 = Math.cos((2 * Math.PI * i) / segments) * size;
          const x2 = Math.sin((2 * Math.PI * nexti) / segments) * size;
          const y2 = Math.cos((2 * Math.PI * nexti) / segments) * size;
          brushSegments.push(x1, y1, 0, x2, y2, 0);
        }
      } else if (shape === "square") {
        const halfSize = size;
        const corners = [
          [-halfSize, -halfSize, 0],
          [halfSize, -halfSize, 0],
          [halfSize, halfSize, 0],
          [-halfSize, halfSize, 0],
        ];

        for (let i = 0; i < 4; i++) {
          const current = corners[i];
          const next = corners[(i + 1) % 4];
          brushSegments.push(...current, ...next);
        }
      }

      const geometry = new LineGeometry();
      geometry.setPositions(brushSegments);

      const material = new LineMaterial({
        color: 0xfb8c00,
        linewidth: thickness,
      });

      material.resolution.set(window.innerWidth, window.innerHeight);

      const brush = new Line2(geometry, material);
      return brush;
    },
    []
  );

  const isVertexWithinDepth = useCallback(
    (vertex, drillCenter, surfaceNormal, depth) => {
      const offset = vertex.clone().sub(drillCenter);
      const depthComponent = offset.dot(surfaceNormal);
      return depthComponent >= -depth;
    },
    []
  );

  const interpolateOnEdgeCircle = useCallback(
    (v1, v2, n1, n2, center, radius, surfaceNormal, depth) => {
      const projectToSurface = (vertex) => {
        const offset = vertex.clone().sub(center);
        const normalComponent = offset.dot(surfaceNormal);
        return vertex
          .clone()
          .sub(surfaceNormal.clone().multiplyScalar(normalComponent));
      };

      const projV1 = projectToSurface(v1);
      const projV2 = projectToSurface(v2);

      const d1 = projV1.distanceTo(center);
      const d2 = projV2.distanceTo(center);

      if (Math.abs(d1 - d2) < 0.0001) return null;

      const t = (radius - d1) / (d2 - d1);
      if (t <= 0 || t >= 1) return null;

      const newVertex = new THREE.Vector3().lerpVectors(v1, v2, t);
      const newNormal = new THREE.Vector3().lerpVectors(n1, n2, t).normalize();

      if (!isVertexWithinDepth(newVertex, center, surfaceNormal, depth)) {
        return null;
      }

      return { vertex: newVertex, normal: newNormal };
    },
    [isVertexWithinDepth]
  );

  const clipTriangleToCircle = useCallback(
    (v1, v2, v3, n1, n2, n3, center, radius, surfaceNormal, depth) => {
      const withinDepth = [
        isVertexWithinDepth(v1, center, surfaceNormal, depth),
        isVertexWithinDepth(v2, center, surfaceNormal, depth),
        isVertexWithinDepth(v3, center, surfaceNormal, depth),
      ];

      const beyondDepthCount = withinDepth.filter((w) => !w).length;
      if (beyondDepthCount === 3) {
        return [{ vertices: [v1, v2, v3], normals: [n1, n2, n3] }];
      }

      const projectToSurface = (vertex) => {
        const offset = vertex.clone().sub(center);
        const normalComponent = offset.dot(surfaceNormal);
        return vertex
          .clone()
          .sub(surfaceNormal.clone().multiplyScalar(normalComponent));
      };

      const projV1 = projectToSurface(v1);
      const projV2 = projectToSurface(v2);
      const projV3 = projectToSurface(v3);

      const distances = [
        projV1.distanceTo(center),
        projV2.distanceTo(center),
        projV3.distanceTo(center),
      ];

      const inside = distances.map((d, i) => d < radius && withinDepth[i]);
      const insideCount = inside.filter(Boolean).length;

      if (insideCount === 3) {
        return [];
      }

      if (insideCount === 0) {
        return [{ vertices: [v1, v2, v3], normals: [n1, n2, n3] }];
      }

      const vertices = [v1, v2, v3];
      const normals = [n1, n2, n3];
      const edges = [
        [0, 1],
        [1, 2],
        [2, 0],
      ];

      const newVertices = [];
      const newNormals = [];

      for (let i = 0; i < 3; i++) {
        if (!inside[i]) {
          newVertices.push(vertices[i]);
          newNormals.push(normals[i]);
        }
      }

      const intersections = [];
      edges.forEach(([i, j]) => {
        if (inside[i] !== inside[j]) {
          const intersection = interpolateOnEdgeCircle(
            vertices[i],
            vertices[j],
            normals[i],
            normals[j],
            center,
            radius,
            surfaceNormal,
            depth
          );
          if (intersection) {
            intersections.push(intersection);
          }
        }
      });

      intersections.forEach((intersection) => {
        newVertices.push(intersection.vertex);
        newNormals.push(intersection.normal);
      });

      if (newVertices.length < 3) return [];
      if (newVertices.length === 3) {
        return [{ vertices: newVertices, normals: newNormals }];
      }

      const resultTriangles = [];
      for (let i = 1; i < newVertices.length - 1; i++) {
        resultTriangles.push({
          vertices: [newVertices[0], newVertices[i], newVertices[i + 1]],
          normals: [newNormals[0], newNormals[i], newNormals[i + 1]],
        });
      }

      return resultTriangles;
    },
    [interpolateOnEdgeCircle, isVertexWithinDepth]
  );

  const processSquareDrill = useCallback(
    (geometry, intersection, size) => {
      const positionAttr = geometry.attributes.position;
      const normalAttr = geometry.attributes.normal;

      const positions = [];
      const normals = [];
      const deletedTrianglesList = [];

      const originalGeometry = {
        positions: Array.from(positionAttr.array),
        normals: Array.from(normalAttr.array),
      };

      const center = intersection.point.clone(); // LOCAL
      const surfaceNormal = intersection.face.normal.clone().normalize(); // LOCAL

      const tangent = new THREE.Vector3().crossVectors(
        surfaceNormal,
        new THREE.Vector3(0, 1, 0)
      );
      if (tangent.length() < 0.01) tangent.set(1, 0, 0);
      tangent.normalize();
      const bitangent = new THREE.Vector3()
        .crossVectors(surfaceNormal, tangent)
        .normalize();

      const isInsideSquare = (vertex) => {
        if (!isVertexWithinDepth(vertex, center, surfaceNormal, drillDepth)) {
          return false;
        }
        const offset = vertex.clone().sub(center);
        const normalComponent = offset.dot(surfaceNormal);
        const projectedVertex = vertex
          .clone()
          .sub(surfaceNormal.clone().multiplyScalar(normalComponent));

        const localOffset = projectedVertex.clone().sub(center);
        const localX = localOffset.dot(tangent);
        const localY = localOffset.dot(bitangent);
        return Math.abs(localX) <= size && Math.abs(localY) <= size;
      };

      const interpolateOnEdge = (v1, v2, n1, n2) => {
        const projectVertex = (vertex) => {
          const offset = vertex.clone().sub(center);
          const normalComponent = offset.dot(surfaceNormal);
          return vertex
            .clone()
            .sub(surfaceNormal.clone().multiplyScalar(normalComponent));
        };

        const projV1 = projectVertex(v1);
        const projV2 = projectVertex(v2);

        const offset1 = projV1.clone().sub(center);
        const offset2 = projV2.clone().sub(center);
        const local1 = new THREE.Vector2(
          offset1.dot(tangent),
          offset1.dot(bitangent)
        );
        const local2 = new THREE.Vector2(
          offset2.dot(tangent),
          offset2.dot(bitangent)
        );

        const clipLine = (a1, a2, limit) => {
          const diff = a2 - a1;
          if (Math.abs(diff) < 0.0001) return null;
          const t = (limit - a1) / diff;
          return t >= 0.001 && t <= 0.999 ? t : null;
        };

        const candidates = [
          clipLine(local1.x, local2.x, size),
          clipLine(local1.x, local2.x, -size),
          clipLine(local1.y, local2.y, size),
          clipLine(local1.y, local2.y, -size),
        ];

        for (let t of candidates) {
          if (t === null) continue;
          const newV = new THREE.Vector3().lerpVectors(v1, v2, t);
          const newN = new THREE.Vector3().lerpVectors(n1, n2, t).normalize();

          if (!isVertexWithinDepth(newV, center, surfaceNormal, drillDepth)) {
            continue;
          }

          if (
            isInsideSquare(newV) ||
            v1.distanceTo(newV) < 0.01 ||
            v2.distanceTo(newV) < 0.01
          ) {
            return { vertex: newV, normal: newN };
          }
        }

        return null;
      };

      const clipTriangleToSquare = (vertices, normals) => {
        const inside = vertices.map(isInsideSquare);
        const insideCount = inside.filter(Boolean).length;

        if (insideCount === 0) {
          return [{ vertices, normals }];
        }

        if (insideCount === 3) {
          return [];
        }

        const clippedVertices = [];
        const clippedNormals = [];

        for (let i = 0; i < 3; i++) {
          if (!inside[i]) {
            clippedVertices.push(vertices[i]);
            clippedNormals.push(normals[i]);
          }
        }

        const edges = [
          [0, 1],
          [1, 2],
          [2, 0],
        ];
        const intersections = [];

        edges.forEach(([i, j]) => {
          if (inside[i] !== inside[j]) {
            const interp = interpolateOnEdge(
              vertices[i],
              vertices[j],
              normals[i],
              normals[j]
            );
            if (interp) {
              intersections.push(interp);
            }
          }
        });

        const uniqueIntersections = [];
        intersections.forEach((intersection) => {
          const isDuplicate = uniqueIntersections.some(
            (existing) => existing.vertex.distanceTo(intersection.vertex) < 0.01
          );
          if (!isDuplicate) {
            uniqueIntersections.push(intersection);
          }
        });

        uniqueIntersections.forEach((intersection) => {
          clippedVertices.push(intersection.vertex);
          clippedNormals.push(intersection.normal);
        });

        if (clippedVertices.length < 3) {
          return [];
        }

        const result = [];
        for (let i = 1; i < clippedVertices.length - 1; i++) {
          result.push({
            vertices: [
              clippedVertices[0],
              clippedVertices[i],
              clippedVertices[i + 1],
            ],
            normals: [
              clippedNormals[0],
              clippedNormals[i],
              clippedNormals[i + 1],
            ],
          });
        }

        return result;
      };

      const processTriangleWithSubdivision = (
        v1,
        v2,
        v3,
        n1,
        n2,
        n3,
        depth = 0
      ) => {
        const maxDepth = 3;

        const edge1 = v1.distanceTo(v2);
        const edge2 = v2.distanceTo(v3);
        const edge3 = v3.distanceTo(v1);
        const maxEdge = Math.max(edge1, edge2, edge3);

        if (depth >= maxDepth || maxEdge < size * 0.25) {
          return clipTriangleToSquare([v1, v2, v3], [n1, n2, n3]);
        }

        const inside = [
          isInsideSquare(v1),
          isInsideSquare(v2),
          isInsideSquare(v3),
        ];

        const insideCount = inside.filter(Boolean).length;

        if (insideCount === 0 || insideCount === 3) {
          return clipTriangleToSquare([v1, v2, v3], [n1, n2, n3]);
        }

        const mid12 = new THREE.Vector3()
          .addVectors(v1, v2)
          .multiplyScalar(0.5);
        const mid23 = new THREE.Vector3()
          .addVectors(v2, v3)
          .multiplyScalar(0.5);
        const mid31 = new THREE.Vector3()
          .addVectors(v3, v1)
          .multiplyScalar(0.5);

        const midN12 = new THREE.Vector3().addVectors(n1, n2).normalize();
        const midN23 = new THREE.Vector3().addVectors(n2, n3).normalize();
        const midN31 = new THREE.Vector3().addVectors(n3, n1).normalize();

        const subTriangles = [
          [v1, mid12, mid31, n1, midN12, midN31],
          [mid12, v2, mid23, midN12, n2, midN23],
          [mid31, mid23, v3, midN31, midN23, n3],
          [mid12, mid23, mid31, midN12, midN23, midN31],
        ];

        const results = [];
        subTriangles.forEach(([sv1, sv2, sv3, sn1, sn2, sn3]) => {
          results.push(
            ...processTriangleWithSubdivision(
              sv1,
              sv2,
              sv3,
              sn1,
              sn2,
              sn3,
              depth + 1
            )
          );
        });

        return results;
      };

      for (let i = 0; i < positionAttr.count; i += 3) {
        const v0 = new THREE.Vector3().fromBufferAttribute(positionAttr, i);
        const v1 = new THREE.Vector3().fromBufferAttribute(positionAttr, i + 1);
        const v2 = new THREE.Vector3().fromBufferAttribute(positionAttr, i + 2);

        const n0 = new THREE.Vector3().fromBufferAttribute(normalAttr, i);
        const n1 = new THREE.Vector3().fromBufferAttribute(normalAttr, i + 1);
        const n2 = new THREE.Vector3().fromBufferAttribute(normalAttr, i + 2);

        const originalTriangle = {
          vertices: Array.from(
            originalGeometry.positions.slice(i * 3, i * 3 + 9)
          ),
          normals: Array.from(originalGeometry.normals.slice(i * 3, i * 3 + 9)),
          triangleIndex: i / 3,
          startIndex: i * 3,
        };

        const clippedTriangles = processTriangleWithSubdivision(
          v0,
          v1,
          v2,
          n0,
          n1,
          n2
        );

        const wasModified =
          clippedTriangles.length === 0 ||
          clippedTriangles.length > 1 ||
          (clippedTriangles.length === 1 &&
            (!clippedTriangles[0].vertices[0].equals(v0) ||
              !clippedTriangles[0].vertices[1].equals(v1) ||
              !clippedTriangles[0].vertices[2].equals(v2)));

        if (wasModified) {
          deletedTrianglesList.push(originalTriangle);
        }

        clippedTriangles.forEach((triangle) => {
          triangle.vertices.forEach((vertex) => {
            positions.push(vertex.x, vertex.y, vertex.z);
          });
          triangle.normals.forEach((normal) => {
            normals.push(normal.x, normal.y, normal.z);
          });
        });
      }

      const newGeometry = new THREE.BufferGeometry();
      newGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3)
      );
      newGeometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(normals, 3)
      );

      const modifiedGeometry = {
        positions: Array.from(positions),
        normals: Array.from(normals),
      };

      console.log(
        `Square drill operation (depth: ${drillDepth}): ${
          deletedTrianglesList.length
        } triangles affected out of ${positionAttr.count / 3} total`
      );

      return {
        newGeometry,
        deletedTrianglesList,
        originalGeometry,
        modifiedGeometry,
      };
    },
    [isVertexWithinDepth, drillDepth]
  );

  const processCircleDrill = useCallback(
    (geometry, intersection, size) => {
      const positions = geometry.attributes.position.array;
      const normals = geometry.attributes.normal.array;
      const deletedTrianglesList = [];
      const newTriangles = [];

      const originalGeometry = {
        positions: Array.from(positions),
        normals: Array.from(normals),
      };

      const center = intersection.point.clone(); // LOCAL
      const radius = size;
      const surfaceNormal = intersection.face.normal.clone().normalize(); // LOCAL

      const processTriangleWithSubdivision = (
        v1,
        v2,
        v3,
        n1,
        n2,
        n3,
        depth = 0
      ) => {
        const maxDepth = 3;

        const edge1 = v1.distanceTo(v2);
        const edge2 = v2.distanceTo(v3);
        const edge3 = v3.distanceTo(v1);
        const maxEdge = Math.max(edge1, edge2, edge3);

        if (depth >= maxDepth || maxEdge < radius * 0.3) {
          return clipTriangleToCircle(
            v1,
            v2,
            v3,
            n1,
            n2,
            n3,
            center,
            radius,
            surfaceNormal,
            drillDepth
          );
        }

        const projectToSurface = (vertex) => {
          const offset = vertex.clone().sub(center);
          const normalComponent = offset.dot(surfaceNormal);
          return vertex
            .clone()
            .sub(surfaceNormal.clone().multiplyScalar(normalComponent));
        };

        const distances = [
          projectToSurface(v1).distanceTo(center),
          projectToSurface(v2).distanceTo(center),
          projectToSurface(v3).distanceTo(center),
        ];

        const withinDepth = [
          isVertexWithinDepth(v1, center, surfaceNormal, drillDepth),
          isVertexWithinDepth(v2, center, surfaceNormal, drillDepth),
          isVertexWithinDepth(v3, center, surfaceNormal, drillDepth),
        ];

        const inside = distances.map((d, i) => d < radius && withinDepth[i]);
        const insideCount = inside.filter(Boolean).length;

        if (insideCount === 0 || insideCount === 3) {
          return clipTriangleToCircle(
            v1,
            v2,
            v3,
            n1,
            n2,
            n3,
            center,
            radius,
            surfaceNormal,
            drillDepth
          );
        }

        const mid12 = new THREE.Vector3()
          .addVectors(v1, v2)
          .multiplyScalar(0.5);
        const mid23 = new THREE.Vector3()
          .addVectors(v2, v3)
          .multiplyScalar(0.5);
        const mid31 = new THREE.Vector3()
          .addVectors(v3, v1)
          .multiplyScalar(0.5);

        const midN12 = new THREE.Vector3().addVectors(n1, n2).normalize();
        const midN23 = new THREE.Vector3().addVectors(n2, n3).normalize();
        const midN31 = new THREE.Vector3().addVectors(n3, n1).normalize();

        const subTriangles = [
          [v1, mid12, mid31, n1, midN12, midN31],
          [mid12, v2, mid23, midN12, n2, midN23],
          [mid31, mid23, v3, midN31, midN23, n3],
          [mid12, mid23, mid31, midN12, midN23, midN31],
        ];

        const results = [];
        subTriangles.forEach(([sv1, sv2, sv3, sn1, sn2, sn3]) => {
          results.push(
            ...processTriangleWithSubdivision(
              sv1,
              sv2,
              sv3,
              sn1,
              sn2,
              sn3,
              depth + 1
            )
          );
        });

        return results;
      };

      for (let i = 0; i < positions.length; i += 9) {
        const v1 = new THREE.Vector3(
          positions[i],
          positions[i + 1],
          positions[i + 2]
        );
        const v2 = new THREE.Vector3(
          positions[i + 3],
          positions[i + 4],
          positions[i + 5]
        );
        const v3 = new THREE.Vector3(
          positions[i + 6],
          positions[i + 7],
          positions[i + 8]
        );

        const n1 = new THREE.Vector3(
          normals[i],
          normals[i + 1],
          normals[i + 2]
        );
        const n2 = new THREE.Vector3(
          normals[i + 3],
          normals[i + 4],
          normals[i + 5]
        );
        const n3 = new THREE.Vector3(
          normals[i + 6],
          normals[i + 7],
          normals[i + 8]
        );

        const originalTriangle = {
          vertices: Array.from(positions.slice(i, i + 9)),
          normals: Array.from(normals.slice(i, i + 9)),
          triangleIndex: i / 9,
          startIndex: i,
        };

        const resultTriangles = processTriangleWithSubdivision(
          v1,
          v2,
          v3,
          n1,
          n2,
          n3
        );

        const wasModified =
          resultTriangles.length === 0 ||
          resultTriangles.length > 1 ||
          (resultTriangles.length === 1 &&
            (!resultTriangles[0].vertices[0].equals(v1) ||
              !resultTriangles[0].vertices[1].equals(v2) ||
              !resultTriangles[0].vertices[2].equals(v3)));

        if (wasModified) {
          deletedTrianglesList.push(originalTriangle);
        }

        newTriangles.push(...resultTriangles);
      }

      const newPositions = [];
      const newNormals = [];

      newTriangles.forEach((triangle) => {
        triangle.vertices.forEach((vertex, idx) => {
          newPositions.push(vertex.x, vertex.y, vertex.z);
          newNormals.push(
            triangle.normals[idx].x,
            triangle.normals[idx].y,
            triangle.normals[idx].z
          );
        });
      });

      const newGeometry = new THREE.BufferGeometry();
      newGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(newPositions, 3)
      );
      newGeometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(newNormals, 3)
      );

      const modifiedGeometry = {
        positions: Array.from(newPositions),
        normals: Array.from(newNormals),
      };

      console.log(
        `Circle drill operation (depth: ${drillDepth}): ${
          deletedTrianglesList.length
        } triangles affected out of ${positions.length / 9} total`
      );

      return {
        newGeometry,
        deletedTrianglesList,
        originalGeometry,
        modifiedGeometry,
      };
    },
    [clipTriangleToCircle, isVertexWithinDepth, drillDepth]
  );

  // --------- CHANGED: make performDrill use LOCAL hit + optional pushUndo
  const performDrill = useCallback(
    (intersect, { pushUndo = true } = {}) => {
      const { object } = intersect;
      const geometry = object.geometry;

      // Convert world-space hit to LOCAL (face.normal is already local)
      const pointLocal = object.worldToLocal(intersect.point.clone());
      const normalLocal = intersect.face.normal.clone().normalize();

      const localIntersection = {
        point: pointLocal,
        face: { normal: normalLocal },
      };

      const result =
        drillShape === "square"
          ? processSquareDrill(geometry, localIntersection, brushSize)
          : processCircleDrill(geometry, localIntersection, brushSize);

      const {
        newGeometry,
        deletedTrianglesList,
        originalGeometry,
        modifiedGeometry,
      } = result;

      if (deletedTrianglesList.length > 0) {
        const drillUndo = {
          type: "drill",
          geometryId: object.name,
          originalGeometry,
          modifiedGeometry,
          // Save LOCAL info for deterministic replay
          intersectPointLocal: pointLocal.clone(),
          normalLocal: normalLocal.clone(),
          brushSize,
          drillShape,
          drillDepth,
        };

        if (pushUndo) {
          undoStack.current = undoStack.current.slice(0, undoIndex.current);
          undoStack.current.push(drillUndo);
          undoIndex.current += 1;
        }

        object.geometry.dispose();
        object.geometry = newGeometry;

        console.log(
          `${drillShape} drill performed (depth: ${drillDepth}): modified ${deletedTrianglesList.length} triangles`
        );
      }
    },
    [
      brushSize,
      drillShape,
      drillDepth,
      undoStack,
      undoIndex,
      processSquareDrill,
      processCircleDrill,
    ]
  );

  const undoDrill = useCallback(
    (objectId, originalGeometry) => {
      if (!modelRef.current) return;

      const object = modelRef.current.children.find(
        (child) => child.name === objectId
      );
      if (!object) {
        console.error(`Object with ID ${objectId} not found.`);
        return;
      }

      if (!originalGeometry) {
        console.error(`Original geometry not found for object: ${objectId}`);
        return;
      }

      const restoredGeometry = new THREE.BufferGeometry();
      restoredGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(originalGeometry.positions, 3)
      );
      restoredGeometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(originalGeometry.normals, 3)
      );

      const oldGeometry = object.geometry;
      object.geometry = restoredGeometry;
      oldGeometry.dispose();

      console.log(`Undo: Restored original geometry for object: ${objectId}`);
    },
    [modelRef]
  );

  const redoDrill = useCallback(
    (objectId, modifiedGeometry) => {
      if (!modelRef.current) return;

      const object = modelRef.current.children.find(
        (child) => child.name === objectId
      );
      if (!object) {
        console.error(`Object with ID ${objectId} not found.`);
        return;
      }

      if (!modifiedGeometry) {
        console.error(`Modified geometry not found for object: ${objectId}`);
        return;
      }

      const restoredGeometry = new THREE.BufferGeometry();
      restoredGeometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(modifiedGeometry.positions, 3)
      );
      restoredGeometry.setAttribute(
        "normal",
        new THREE.Float32BufferAttribute(modifiedGeometry.normals, 3)
      );

      const oldGeometry = object.geometry;
      object.geometry = restoredGeometry;
      oldGeometry.dispose();

      console.log(`Redo: Restored modified geometry for object: ${objectId}`);
    },
    [modelRef]
  );

  const onMouseMove = useCallback(
    (e) => {
      if (e.target.closest(".ui-outside-scene")) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const mouse = new THREE.Vector2(
        ((mouseX - viewport.left) / viewport.width) * 2 - 1,
        -((mouseY - viewport.top) / viewport.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, cameraRef.current);
      const visibleObjects = modelRef.current.children.filter(
        (child) => child.visible
      );
      const intersects = raycaster.intersectObjects(visibleObjects, true);

      if (intersects.length > 0) {
        const intersect = intersects[0];
        drillBrushRef.current.position.copy(intersect.point);
        drillBrushRef.current.lookAt(
          intersect.point.clone().add(intersect.face.normal)
        );
        setBrushActive(true);
      } else {
        setBrushActive(false);
      }
    },
    [cameraRef, containerRef, modelRef, viewportRef]
  );

  // --------- CHANGED: auto drill takes LOCAL data + optional pushUndo/normalLocal
  const performDrillAuto = useCallback(
    (
      modelName,
      intersectPointLocal,
      brushSize,
      shape = "circle",
      { pushUndo = true, normalLocal = new THREE.Vector3(0, 0, 1) } = {}
    ) => {
      console.log(`Performing ${shape} drill operation on model:`, modelName);
      if (!modelRef.current || !sceneRef.current) {
        console.error("Model or scene reference not found.");
        return;
      }

      const targetModel = modelRef.current.children.find(
        (child) => child.name === modelName
      );

      if (!targetModel) {
        console.error(`Model with name "${modelName}" not found.`);
        return;
      }

      const geometry = targetModel.geometry;
      const positionAttribute = geometry.attributes.position;
      const normalAttribute = geometry.attributes.normal;

      if (!positionAttribute || !normalAttribute) {
        console.error("Geometry attributes missing.");
        return;
      }

      // Expect LOCAL intersection for deterministic replay
      const fakeIntersection = {
        point: intersectPointLocal.clone(),
        face: { normal: normalLocal.clone().normalize() },
      };

      const result =
        shape === "square"
          ? processSquareDrill(geometry, fakeIntersection, brushSize)
          : processCircleDrill(geometry, fakeIntersection, brushSize);

      const {
        newGeometry,
        deletedTrianglesList,
        originalGeometry,
        modifiedGeometry,
      } = result;

      if (deletedTrianglesList.length > 0) {
        const drillUndo = {
          type: "drill",
          geometryId: targetModel.name,
          originalGeometry,
          modifiedGeometry,
          intersectPointLocal: intersectPointLocal.clone(),
          normalLocal: normalLocal.clone(),
          brushSize,
          drillShape: shape,
          drillDepth,
        };

        if (pushUndo) {
          undoStack.current = undoStack.current.slice(0, undoIndex.current);
          undoStack.current.push(drillUndo);
          undoIndex.current += 1;
        }

        targetModel.geometry.dispose();
        targetModel.geometry = newGeometry;

        console.log(
          `Auto ${shape} drill performed (depth: ${drillDepth}): modified ${deletedTrianglesList.length} triangles on model: ${targetModel.name}`
        );
      }
    },
    [
      modelRef,
      sceneRef,
      undoStack,
      undoIndex,
      processSquareDrill,
      processCircleDrill,
      drillDepth,
    ]
  );

  const onMouseDown = useCallback(
    (e) => {
      if (e.button !== 0 || e.target.closest(".ui-outside-scene")) {
        return;
      }
      if (!brushActive) return;

      const rect = containerRef.current.getBoundingClientRect();
      
      // Use viewport dimensions if available, otherwise use container dimensions
      const viewport = viewportRef?.current || {
        left: 0,
        top: 0,
        width: rect.width,
        height: rect.height,
      };
      
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const mouse = new THREE.Vector2(
        ((mouseX - viewport.left) / viewport.width) * 2 - 1,
        -((mouseY - viewport.top) / viewport.height) * 2 + 1
      );

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(mouse, cameraRef.current);
      const visibleObjects = modelRef.current.children.filter(
        (child) => child.visible
      );
      const intersects = raycaster.intersectObjects(visibleObjects, true);

      if (intersects.length > 0) {
        performDrill(intersects[0]); // default pushUndo = true
      }
    },
    [brushActive, cameraRef, containerRef, modelRef, performDrill, viewportRef]
  );

  useEffect(() => {
    if (isDrillMode) {
      const brush = initializeBrush(brushSize, 5, drillShape);
      if (drillBrushRef.current) {
        sceneRef.current.remove(drillBrushRef.current);
      }
      sceneRef.current.add(brush);
      drillBrushRef.current = brush;

      const onResize = () => {
        if (drillBrushRef.current?.material?.resolution) {
          drillBrushRef.current.material.resolution.set(
            window.innerWidth,
            window.innerHeight
          );
        }
      };

      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mousedown", onMouseDown);
      window.addEventListener("resize", onResize);

      return () => {
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("resize", onResize);
      };
    } else {
      if (drillBrushRef.current) {
        sceneRef.current.remove(drillBrushRef.current);
        drillBrushRef.current = null;
      }
    }
  }, [
    isDrillMode,
    brushSize,
    drillShape,
    initializeBrush,
    onMouseMove,
    onMouseDown,
    sceneRef,
  ]);

  return {
    isDrillMode,
    setIsDrillMode,
    brushSize,
    setBrushSize,
    drillShape,
    setDrillShape,
    drillDepth,
    undoDrill,
    redoDrill,
    performDrillAuto,
  };
};

export default useDrillMode;
